import {
  Box,
  Button,
  Flex,
  Grid,
  LoadingOverlay,
  Modal,
  Select,
  Text
} from "@dehub/theme";
import { i18n, nameOptionsFilter, useForm } from "@dehub/theme";
import { yupResolver } from "mantine-form-yup-resolver";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import * as yup from "yup";
import { WarehouseStaffApi } from "@/apis";
import type { IWarehouseStaffRole, IWarehouseStaffScope } from "@/apis/warehouseStaff";
import type { IGeneral } from "@/models/General";
import type { IWarehouseStaffRow } from "@/models/Warehouse";
import HttpHandler from "@/utils/http-handler";
import { message } from "@/utils/message";
import { renderName } from "@/utils/render-name";
import { useWarehouseStaffPermissions } from "../hooks/useWarehouseStaffPermissions";
import { FlagSwitch, RoleCards } from "./StaffFormParts";
import { SUB_ROLE_CODES, WAREHOUSE_STAFF_ROLES, WAREHOUSE_STAFF_SCOPES, getRoleLabel, getScopeLabel } from "../constants";

type IFormData = {
  userId: string;
  staffRole: string;
  scope: string;
  canManageStaff: boolean;
  isPrimary: boolean;
};

const schema = yup.object({
  userId   : yup.string().required(i18n.t("common:required")),
  staffRole: yup.string().required(i18n.t("common:required")),
  scope    : yup.string().oneOf(["SINGLE", "MULTI"]).required(),
  canManageStaff: yup.boolean(),
  isPrimary: yup.boolean()
});

interface Props {
  opened: boolean;
  warehouseId: string;
  target?: IWarehouseStaffRow | null;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

export function AddStaffModal({ opened, warehouseId, target, onClose, onSuccess }: Props) {
  const { t } = useTranslation("staff");
  const isEdit = !!target;
  const perms = useWarehouseStaffPermissions(warehouseId);
  const partnerStaffs = useSelector((state: { general: IGeneral }) => state.general.partnerStaffs);
  const [loading, setLoading] = useState(false);

  const staffOptions = useMemo(
    () =>
      (partnerStaffs as unknown as Array<{ id: string; firstName?: string; lastName?: string }> | undefined)?.map(
        (c) => ({
          label: renderName(c),
          value: c.id
        })
      ) ?? [],
    [partnerStaffs]
  );

  const roleOptions = useMemo(() => {
    const allowed = perms.isManager
      ? WAREHOUSE_STAFF_ROLES.map((r) => r.code)
      : perms.isKeeperWithDelegation
        ? SUB_ROLE_CODES
        : [];
    return WAREHOUSE_STAFF_ROLES.filter((r) => allowed.includes(r.code)).map((r) => ({
      label: getRoleLabel(t, r.code),
      value: r.code
    }));
  }, [perms.isManager, perms.isKeeperWithDelegation, t]);

  const form = useForm<IFormData>({
    initialValues: {
      userId        : "",
      staffRole     : "",
      scope         : "SINGLE",
      canManageStaff: false,
      isPrimary     : false
    },
    validate: yupResolver(schema)
  });

  // Reset form when opening
  useEffect(() => {
    if (!opened) return;
    if (target) {
      form.setValues({
        userId        : target.userId,
        staffRole     : target.staffRole,
        scope         : target.scope,
        canManageStaff: target.canManageStaff,
        isPrimary     : target.isPrimary
      });
    } else {
      form.reset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, target?.id]);

  const role = form.values.staffRole;
  const isKeeperRole = role === "WAREHOUSE_KEEPER";
  const canShowScope = perms.isManager;
  const canShowManageFlag = perms.isManager && isKeeperRole;
  const canShowPrimary = perms.isManager && isKeeperRole;

  // Side-effect: role өөрчлөгдөхөд keeper-only flag-уудыг reset
  useEffect(() => {
    if (!isKeeperRole) {
      if (form.values.canManageStaff) form.setFieldValue("canManageStaff", false);
      if (form.values.isPrimary) form.setFieldValue("isPrimary", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isKeeperRole]);

  const handleSubmit = async (values: IFormData) => {
    setLoading(true);
    try {
      const payload = {
        userId        : values.userId,
        staffRole     : values.staffRole as IWarehouseStaffRole,
        scope         : values.scope as IWarehouseStaffScope,
        canManageStaff: values.canManageStaff,
        isPrimary     : values.isPrimary
      };
      if (isEdit && target) {
        // PUT schema has no userId (the person of a staff row cannot change): sending it is a 400.
        const { userId: _userId, ...changes } = payload;
        await WarehouseStaffApi.update(warehouseId, target.id, changes);
        message.success(t("msg.updateSuccess"));
      } else {
        await WarehouseStaffApi.create(warehouseId, payload);
        message.success(t("msg.addSuccess"));
      }
      await onSuccess();
    } catch (err) {
      message.error((err as HttpHandler)?.message ?? t("msg.genericError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      title={isEdit ? t("warehouse.editTitle") : t("warehouse.addTitle")}
      size="md">
      <Box pos="relative">
        <LoadingOverlay visible={loading} />
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Grid>
            <Grid.Col span={12}>
              <Select
                label={t("warehouse.user")}
                placeholder={t("warehouse.userPlaceholder")}
                searchable
                filter={nameOptionsFilter}
                data={staffOptions}
                withAsterisk
                disabled={isEdit}
                {...form.getInputProps("userId")}
              />
            </Grid.Col>
            <Grid.Col span={12}>
              <Text fz={14} fw={500} mb={6}>
                {t("warehouse.role")} <Text span c="red.8">*</Text>
              </Text>
              <RoleCards
                t={t}
                roles={roleOptions.map((r) => r.value as IWarehouseStaffRole)}
                value={form.values.staffRole}
                onChange={(role) => form.setFieldValue("staffRole", role)}
                error={form.errors.staffRole as string | undefined}
              />
            </Grid.Col>
            {canShowScope ? (
              <Grid.Col span={12}>
                <Select
                  label={t("warehouse.scope")}
                  data={WAREHOUSE_STAFF_SCOPES.map((s) => ({ label: getScopeLabel(t, s.code), value: s.code }))}
                  description={t("warehouse.scopeDescription")}
                  {...form.getInputProps("scope")}
                />
              </Grid.Col>
            ) : null}
            {canShowManageFlag || canShowPrimary ? (
              <Grid.Col span={12}>
                <Text fz={14} fw={500} mb={6}>
                  {t("warehouse.specialRights")}
                </Text>
                <Flex direction="column" gap="xs">
                  {canShowPrimary ? (
                    <FlagSwitch
                      label={t("warehouse.primary")}
                      description={t("warehouse.primaryDescription")}
                      checked={form.values.isPrimary}
                      onChange={(checked) => form.setFieldValue("isPrimary", checked)}
                    />
                  ) : null}
                  {canShowManageFlag ? (
                    <FlagSwitch
                      label={t("warehouse.manageFlag")}
                      description={t("warehouse.manageFlagDescription")}
                      checked={form.values.canManageStaff}
                      onChange={(checked) => form.setFieldValue("canManageStaff", checked)}
                    />
                  ) : null}
                </Flex>
              </Grid.Col>
            ) : null}
          </Grid>

          <Flex justify="flex-end" gap="sm" mt="md">
            <Button variant="default" onClick={onClose} type="button">
              {t("common:actions.cancel")}
            </Button>
            <Button type="submit" loading={loading}>
              {isEdit ? t("common:actions.save") : t("common:actions.add")}
            </Button>
          </Flex>
        </form>
      </Box>
    </Modal>
  );
}
